/* eslint-disable max-lines-per-function */
import React, { useContext } from "react";
import { useFormikContext } from "formik";
import { Link } from "react-router-dom";
import { List, ListItemButton, ListItemIcon, ListItemText, Typography, Divider, Box } from "@mui/material";
import { usePostgresViewer } from "components/viewer/context";
import { drawerWidth } from "components/header/layout/helpers";

import { FormValues } from "pages/eligibility/type";
import { QuestionProps } from "pages/eligibility/components/question";
import SaveProgress from "pages/eligibility/components/context/save-progress";
import styles from "../../layout.module.css";

import { keyFromIdentity, findMatchingSection, getErrorCount } from "../helpers";
import { MobileMenu } from "./form-navigation";

type groupProps = {
  name: string;
  title: string;
  questions: string[][];
}

type MenuProps = {
  mobileMenu: MobileMenu;
  groups: groupProps[];
  section: string | undefined;
  path: { match?: { submissionId?: string } };
  questionsMap: Record<string, QuestionProps>;
}
type SectionQuestions = Record<string, string>;

const FormSidebar: React.FC<MenuProps> = ({ groups, section, path, questionsMap, mobileMenu }) => {
  const viewer = usePostgresViewer();

  const form = useFormikContext<FormValues>();

  const addSectionQuestion =
  (acc: SectionQuestions, questionIdentity: string[], name: string): SectionQuestions => {
    const questionName = keyFromIdentity(questionIdentity);
    const { children } = questionsMap[questionName];

    return children.reduce(
      (childAcc: SectionQuestions, childIdentity: string[]) =>
        addSectionQuestion(childAcc, childIdentity, name),
      { ...acc, [questionName]: name },
    );
  };

  const questionSections = groups.reduce((acc: SectionQuestions, { name, questions }: groupProps) =>
    questions.reduce((childAcc, questionIdentity) =>
      addSectionQuestion(childAcc, questionIdentity, name), acc), {});

  const questionIdentities = Object.keys(questionSections);

  const sectionErrorCounts = Object.keys(form.errors).reduce((errors, errIdentity) => {
    const sectionName = findMatchingSection(questionIdentities, questionSections, errIdentity);
    if (!sectionName) return errors;
    return {
      ...errors,
      [sectionName]: (errors[sectionName] || 0) + getErrorCount(form.errors[errIdentity]),
    };
  }, {} as Record<string, number>);

  const saveProgress = useContext(SaveProgress);

  return (
    <div style={{ width: `${drawerWidth}px`, height: "100vh", display: "flex", flexDirection: "column", overflow: "hidden" }}>
      <Box sx={{
        display: "flex",
        alignItems: "center",
        paddingLeft: "24px",
        backgroundColor: "#324257",
        color: "white",
        minHeight: 64,
      }}>
        <Typography variant="h6">Your Application</Typography>
      </Box>

      <Divider />

      <List dense sx={{ flex: 1, overflowY: "auto" }}>
        {groups.map(({ name, title: text }: { name: string, title: string }) => (
          <Link key={`section-${name}`} to={`/application/${path.match?.submissionId}/${name}`}>
            <ListItemButton
              style={{
                flexDirection: "row",
                backgroundColor: name === section ? "rgba(0, 0, 0, 0.08)" : "transparent",
              }}
              selected={name === section}
              onClick={() => {
                if (mobileMenu.isMobile) {
                  mobileMenu.toggle();
                }
                if (viewer) saveProgress();
              }}>
              <ListItemText primary={text} />

              <ListItemIcon>
                {sectionErrorCounts && (
                  <div className="color-hc-error">
                    {sectionErrorCounts[name]}
                  </div>
                )}
              </ListItemIcon>
            </ListItemButton>
          </Link>
        ))}
      </List>
      <div className={styles.footer} style={{ marginTop: "auto", position: "static" }}>&nbsp;</div>
    </div>
  );
};

export default FormSidebar;
